import { formatTenantDate } from 'src/app/shared/utils/tenant-date';
import { Component, ElementRef, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import {
  CompanyPolicy,
  getPolicyCategoryColor,
  getPolicyStatusColor,
} from '../../models/policy-management.models';
import { ProceedModalComponent } from '../proceed-modal/proceed-modal.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { sanitizeRichText } from '../../utils/rich-text-sanitizer';

@Component({
  selector: 'app-policy-reader-modal',
  templateUrl: './policy-reader-modal.component.html',
  styleUrls: ['./policy-reader-modal.component.css'],
})
export class PolicyReaderModalComponent implements OnInit {
  policy: CompanyPolicy;
  contentHtml: SafeHtml = '';
  staffId: string;
  staffName: string;
  staffEmail: string;
  isAcknowledged: boolean;
  acknowledgedAt?: string;

  showAcknowledgmentButton: boolean = false;
  /** False when the viewer can't sign (e.g. looking at someone else's profile). */
  canAcknowledge: boolean = true;
  acknowledging = false;

  constructor(
    private dialogRef: MatDialogRef<PolicyReaderModalComponent>,
    private dialog: MatDialog,
    private elementRef: ElementRef<HTMLElement>,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.policy = data.policy;
    // Keeps the rich-text formatting (alignment, colours, fonts, lists, tables) the policy was written with.
    this.contentHtml = this.sanitizer.bypassSecurityTrustHtml(sanitizeRichText(data.policy?.content));
    this.staffId = data.staffId;
    this.staffName = data.staffName;
    this.staffEmail = data.staffEmail;
    this.isAcknowledged = data.isAcknowledged || false;
    this.acknowledgedAt = data.acknowledgedAt;
    this.canAcknowledge = data.canAcknowledge !== false;
  }

  ngOnInit(): void {
    this.showAcknowledgmentButton = this.policy.requiresAcknowledgment && !this.isAcknowledged && this.canAcknowledge;
  }

  onAcknowledge(): void {
    this.dialog.open(ProceedModalComponent, {
      disableClose: true,
      panelClass: 'entry-dialog',
      width: '420px',
      maxWidth: '95vw',
      data: {
        message: $localize`:@@confirmAcknowledge:Do you acknowledge that you have read and understood "${this.policy.title}:title: ${this.policy.version}:version:"?`,
        proceedVal: {
          name: $localize`:@@acknowledge:Acknowledge`,
          class: 'p-3 rounded-xl text-white bg-primary hover:bg-primary/90 w-[150px]',
        },
        cancelVal: {
          name: $localize`:@@cancel:Cancel`,
          class: 'p-3 rounded-xl border border-primary text-primary w-[150px]',
        },
      },
    }).afterClosed().pipe(filter(Boolean)).subscribe(() => {
      // Callers that record the sign-off provide `data.acknowledge` (an Observable); it signs as the logged-in user.
      if (typeof this.data.acknowledge === 'function') {
        if (this.acknowledging) return;
        this.acknowledging = true;
        this.data.acknowledge().subscribe({
          next: (res: any) => {
            this.acknowledging = false;
            if (res?.alreadyAcknowledged) this.toastr.info($localize`:@@policyAlreadyAcknowledged:You have already acknowledged this policy.`);
            else this.toastr.success($localize`:@@policyAcknowledged:Policy acknowledged.`);
            this.dialogRef.close({ acknowledged: true, acknowledgedAt: res?.acknowledgedOn || new Date().toISOString() });
          },
          error: (err: any) => {
            this.acknowledging = false;
            this.toastr.error(err?.message || $localize`:@@somethingWentWrong:Something went wrong. Please try again.`);
          },
        });
        return;
      }
      this.dialogRef.close({ acknowledged: true, acknowledgedAt: new Date().toISOString() });
    });
  }

  onCopy(): void {
    const content = this.getPlainTextContent();
    navigator.clipboard.writeText(content).then(() => {
      this.toastr.success($localize`:@@contentCopied:Policy content copied to clipboard`);
    });
  }

  onPrint(): void {
    const reader = this.elementRef.nativeElement.querySelector('.policy-reader-container');
    if (!reader) {
      this.toastr.error($localize`:@@unableToPrintPolicy:Unable to prepare this policy for printing`);
      return;
    }

    const printable = reader.cloneNode(true) as HTMLElement;
    printable.querySelectorAll('app-modal-header-ui, .reader-footer').forEach(element => element.remove());

    const frame = document.createElement('iframe');
    frame.setAttribute('title', $localize`:@@policyPrintPreview:Policy print preview`);
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    frame.style.visibility = 'hidden';
    document.body.appendChild(frame);

    const printDocument = frame.contentDocument;
    const printWindow = frame.contentWindow;
    if (!printDocument || !printWindow) {
      frame.remove();
      this.toastr.error($localize`:@@unableToPrintPolicy:Unable to prepare this policy for printing`);
      return;
    }

    printDocument.open();
    printDocument.write(`<!doctype html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${this.escapeHtml(this.policy.title)}</title>
          <style>${this.printStyles}</style>
        </head>
        <body>${printable.outerHTML}</body>
      </html>`);
    printDocument.close();

    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      window.setTimeout(() => frame.remove(), 500);
    }, 150);
  }

  private escapeHtml(value: string): string {
    const element = document.createElement('div');
    element.textContent = value;
    return element.innerHTML;
  }

  private readonly printStyles = `
    @page { size: A4 portrait; margin: 16mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { color: #3a3a3a; font-family: Arial, Helvetica, sans-serif; font-size: 10.5pt; line-height: 1.5; }
    .policy-reader-container { display: block; width: 100%; max-height: none; background: #fff; }
    .reader-header { position: static; display: block; padding: 0 0 14px; border-bottom: 1px solid #3a3a3a; background: #fff; }
    .header-badges { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 9px; }
    .category-badge, .version-badge, .status-badge { display: inline-block; padding: 3px 8px; border: 1px solid #999; border-radius: 4px; font-size: 8pt; font-weight: 500; color: #3a3a3a; }
    .category-badge { text-transform: uppercase; }
    .policy-title { margin: 0; color: #3a3a3a; font-size: 20pt; line-height: 1.25; }
    .reader-metadata { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 9px 20px; padding: 14px 0; border-bottom: 1px solid #d5d5d5; background: #fff; }
    .metadata-item { display: flex; align-items: flex-start; gap: 5px; min-width: 0; }
    .metadata-item mat-icon { display: none; }
    .metadata-label { color: #717171; font-size: 9pt; font-weight: 400; }
    .metadata-value { margin-left: 4px; color: #3a3a3a; font-size: 9pt; font-weight: 600; }
    .acknowledgment-status { display: flex; gap: 10px; margin: 16px 0; padding: 12px; border: 1px solid #999; border-radius: 5px; break-inside: avoid; }
    .acknowledgment-status mat-icon { display: none; }
    .acknowledgment-status h3, .acknowledgment-status p { margin: 0; color: #3a3a3a; }
    .reader-body { display: block; overflow: visible; padding: 18px 0 0; }
    .summary-section, .takeaways-section { margin: 0 0 18px; break-inside: avoid; }
    .summary-section h2, .takeaways-section h2 { margin: 0 0 7px; color: #3a3a3a; font-size: 12pt; }
    .summary-text, .takeaways-list, .takeaways-list li { color: #3a3a3a; font-size: 10pt; line-height: 1.5; }
    .summary-text { margin: 0; }
    .takeaways-list { margin: 0; padding-left: 20px; }
    .takeaways-list li { margin: 0 0 4px; }
    .content-section { margin: 0; }
    .content-body { color: #3a3a3a; font-size: 10.5pt; line-height: 1.6; overflow-wrap: anywhere; }
    .content-body h1, .content-body h2, .content-body h3, .content-body h4 { color: #3a3a3a; margin: 16px 0 7px; line-height: 1.3; break-after: avoid; }
    .content-body h1 { font-size: 18pt; }
    .content-body h2 { font-size: 15pt; }
    .content-body h3 { font-size: 12pt; }
    .content-body h4 { font-size: 11pt; }
    .content-body p { margin: 0 0 10px; orphans: 3; widows: 3; }
    .content-body ul, .content-body ol { margin: 0 0 10px; padding-left: 22px; }
    .content-body table { width: 100%; margin: 12px 0; border-collapse: collapse; font-size: 9pt; }
    .content-body tr { break-inside: avoid; }
    .content-body th, .content-body td { padding: 7px; border: 1px solid #3a3a3a; text-align: left; vertical-align: top; }
    .content-body th { background: #f3f5f7; font-weight: 600; }
    .signature-section { margin-top: 20px; padding: 14px; border: 1px solid #4d5d76; border-radius: 5px; background: #fff; break-inside: avoid; }
    .signature-section h3 { margin: 0 0 9px; font-size: 12pt; }
    .signature-box p { margin: 3px 0; color: #3a3a3a; font-size: 9.5pt; }
    .print-policy-title { display: block; margin: 8px 0 0; color: #3a3a3a; font-size: 20pt; line-height: 1.25; }
    app-modal-header-ui, .reader-footer { display: none !important; }
  `;

  getPlainTextContent(): string {
    const div = document.createElement('div');
    div.innerHTML = this.policy.content;
    return div.textContent || div.innerText || '';
  }

  getCategoryColor(): string {
    return getPolicyCategoryColor(this.policy.category);
  }

  getStatusColor(): string {
    return getPolicyStatusColor(this.policy.status);
  }

  formatDate(dateString: string): string {
    return formatTenantDate(dateString);
  }

  close(): void {
    this.dialogRef.close();
  }
}
